/**
 * AssetImage — renders a listing image for the dashboard.
 *
 * The dashboard authenticates with a bearer header, which an <img src> can't
 * carry, so unpublished previews are fetched through the API client as a
 * blob and shown via an object URL. Published assets use the anonymous
 * /files URL directly (no fetch, browser-cacheable).
 */
import { useEffect, useState } from 'react';
import api from '../services/api';
import { assetPreviewUrl, publicFileUrl } from '../services/listings';

interface AssetImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  boardId: number;
  assetId: number;
  /** When true the anonymous URL is used and no authenticated fetch happens. */
  published?: boolean;
}

export function AssetImage({ boardId, assetId, published = false, alt = '', ...rest }: AssetImageProps) {
  const [src, setSrc] = useState<string | null>(published ? publicFileUrl(assetId) : null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (published) {
      setSrc(publicFileUrl(assetId));
      return undefined;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    api.get(assetPreviewUrl(boardId, assetId), { responseType: 'blob', baseURL: '' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [boardId, assetId, published]);

  if (failed) return <span title="Preview unavailable">⚠︎</span>;
  if (!src) return <span aria-busy="true" />;
  return <img src={src} alt={alt} {...rest} />;
}

export default AssetImage;
